import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Eye, Send, Mail, Trash2, UserCheck, MessageSquare } from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { Overline } from "../../components/Layout";
import { api } from "../../lib/api";
import { fmtTimePT, fmtDatePT } from "../../lib/time";
import CapitalRequestDrawer from "../../components/CapitalRequestDrawer";
import { resolveImageUrl } from "../../lib/media";
import { sessionTitle } from "../../lib/sessionTitle";
import { useUrlState } from "../../lib/useUrlState";

export default function AdminSessions() {
  const [seriesList, setSeriesList] = useState([]);
  // Which series you are looking at rides the URL (?series=<id>), so "the
  // requests for this series" is a link (see lib/useUrlState). The selection is
  // DERIVED from the list rather than stored: an unknown or absent id falls back
  // to the first series, which is the old default-to-first behaviour.
  const [seriesId, setSeriesId] = useUrlState("series", "", { push: true });
  const [slots, setSlots] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  // The request drawer stays local: it is opened from several different lists
  // (submissions, booked slots), so there is no single id to resolve it from.
  const [drawerSub, setDrawerSub] = useState(null);

  const active = seriesList.find((s) => s.id === seriesId) ?? seriesList[0] ?? null;

  const loadSeries = () => api.get("/sessions/series").then((r) => setSeriesList(r.data));
  useEffect(() => { loadSeries(); }, []);

  const loadSlots = () => active && api.get(`/sessions/series/${active.id}/slots`).then((r) => setSlots(r.data));
  const loadSubs = () => active && api.get(`/sessions/series/${active.id}/submissions`).then((r) => setSubmissions(r.data));
  useEffect(() => { loadSlots(); loadSubs(); }, [active?.id]);

  const decide = async (subId, status, reason = "") => {
    try {
      await api.post(`/submissions/${subId}/decide`, { status, reason });
      toast.success(status === "accepted" ? "Approved" : "Declined");
      loadSlots(); loadSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const [sendingSlot, setSendingSlot] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [checkinSlot, setCheckinSlot] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null); // { slot, bookerName, bookerEmail }
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNotify, setCancelNotify] = useState(true);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [fbSummary, setFbSummary] = useState(null);
  const [fbSlot, setFbSlot] = useState(null);
  const [fbBulk, setFbBulk] = useState(false);

  const loadFb = () => active && api.get(`/admin/sessions/series/${active.id}/feedback`).then((r) => setFbSummary(r.data)).catch(() => setFbSummary(null));
  useEffect(() => { loadFb(); /* eslint-disable-next-line */ }, [active]);

  const sendFeedbackForSlot = async (slotId) => {
    setFbSlot(slotId);
    try {
      const r = await api.post(`/admin/sessions/series/${active.id}/feedback/send`, { audience: "checked_in", slot_id: slotId });
      const sent = r.data.sent;
      const skipped = r.data.skipped;
      if (sent > 0) toast.success("Feedback survey sent");
      else if (skipped > 0) toast.info("Already sent to this founder");
      else toast.error("Nothing sent — no email on file");
      loadFb();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send feedback");
    } finally { setFbSlot(null); }
  };
  const sendFeedbackBulk = async (audience) => {
    if (!active) return;
    const scope = audience === "checked_in" ? "checked-in founders" : "all booked founders";
    if (!window.confirm(`Send NPS feedback survey to ${scope} for "${active.investor_name || active.title}"?`)) return;
    setFbBulk(true);
    try {
      const r = await api.post(`/admin/sessions/series/${active.id}/feedback/send`, { audience });
      const { sent, skipped, bounced, attempted } = r.data;
      toast.success(`${sent} sent · ${skipped} already sent · ${bounced} failed (of ${attempted})`);
      loadFb();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk feedback send failed");
    } finally { setFbBulk(false); }
  };

  const sendConfirm = async (slotId) => {
    setSendingSlot(slotId);
    try {
      const r = await api.post(`/sessions/slots/${slotId}/send-confirmation`);
      toast.success(`Confirmation sent to ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSendingSlot(null); }
  };
  const sendAllConfirms = async () => {
    if (!active) return;
    if (!window.confirm(`Send confirmation emails to all ${booked.length} booked founders?`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post(`/sessions/series/${active.id}/send-confirmations`);
      const { sent, count } = r.data;
      toast.success(`${sent} of ${count} confirmation${count === 1 ? "" : "s"} sent`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk send failed");
    } finally { setBulkBusy(false); }
  };

  const openCancel = (slot, bookerName, bookerEmail) => {
    setCancelTarget({ slot, bookerName, bookerEmail });
    setCancelReason("");
    setCancelNotify(!!bookerEmail);
  };
  const closeCancel = () => { setCancelTarget(null); setCancelReason(""); setCancelBusy(false); };

  const toggleCheckin = async (slot) => {
    setCheckinSlot(slot.id);
    const path = slot.checked_in ? "undo-checkin" : "checkin";
    try {
      await api.post(`/sessions/slots/${slot.id}/${path}`);
      toast.success(slot.checked_in ? "Check-in reversed" : "Checked in");
      loadSlots();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setCheckinSlot(null); }
  };
  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setCancelBusy(true);
    try {
      const r = await api.post(`/sessions/slots/${cancelTarget.slot.id}/cancel`, {
        reason: cancelReason.trim(),
        notify: cancelNotify,
      });
      const notified = r.data.notified;
      if (notified === "sent") toast.success(`Removed · ${cancelTarget.bookerEmail} notified`);
      else if (notified === "skipped") toast.success("Removed · booker not notified");
      else toast.success("Removed (email delivery pending)");
      closeCancel();
      loadSlots(); loadSubs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
      setCancelBusy(false);
    }
  };

  const pending = submissions.filter((s) => s.status === "pending");
  const booked = slots.filter((s) => s.status === "booked");

  return (
    <AdminShell testId="admin-sessions-page" eyebrow="Founder Labs" title="Session series">

        <div className="mt-10 grid lg:grid-cols-[320px_1fr] gap-6">
          {/* series list */}
          <div className="space-y-3">
            {seriesList.map((s) => (
              <button
                key={s.id}
                onClick={() => setSeriesId(s.id)}
                className={`w-full text-left glass rounded-2xl p-4 hover-lift ${active?.id === s.id ? "border-primary/60" : ""}`}
                data-testid={`admin-series-${s.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl overflow-hidden">
                    <img src={resolveImageUrl(s.investor_photo)} alt="" className="w-full h-full object-cover object-top" />
                  </div>
                  <div>
                    <div className="font-heading text-sm line-clamp-2" title={sessionTitle(s)}>{sessionTitle(s)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.investor_name}</div>
                    <div className="text-xs text-muted-foreground">{s.investor_firm}</div>
                  </div>
                </div>
                <div className="mt-3 flex gap-1.5">
                  <span className="chip text-[10px]">{s.booking_mode}</span>
                </div>
              </button>
            ))}
          </div>

          {/* details */}
          <div className="space-y-6">
            {active && (
              <>
                <div className="glass rounded-2xl p-6">
                  <Overline>Pending approvals</Overline>
                  <div className="mt-4 space-y-2">
                    {pending.length === 0 ? (
                      <div className="text-sm text-muted-foreground">No pending requests.</div>
                    ) : (
                      pending.map((s) => (
                        <div key={s.id} className="flex items-start justify-between p-4 rounded-2xl card-cream" data-testid={`pending-${s.id}`}>
                          <button className="flex-1 text-left" onClick={() => setDrawerSub(s)} data-testid={`open-request-${s.id}`}>
                            <div className="font-medium text-sm">{s.name || "Founder"}</div>
                            <div className="text-xs text-muted-foreground font-mono">{s.email}</div>
                            <div className="text-xs text-muted-foreground mt-1 font-mono">{fmtDatePT(s.slot_start_at, { weekday: "short", month: "short", day: "numeric" })} · {fmtTimePT(s.slot_start_at)} PT</div>
                            {s.pitch_note && <div className="text-xs mt-2 text-foreground/80 italic line-clamp-2">&ldquo;{s.pitch_note}&rdquo;</div>}
                          </button>
                          <div className="flex flex-col gap-1 ml-3">
                            <button onClick={() => setDrawerSub(s)} className="btn-ghost text-xs" data-testid={`view-slot-${s.id}`}><Eye size={11} />Details</button>
                            <button onClick={() => decide(s.id, "accepted")} className="btn-primary text-xs" data-testid={`approve-slot-${s.id}`}><Check size={11} />Approve</button>
                            <button onClick={() => decide(s.id, "declined")} className="btn-ghost text-xs text-destructive" data-testid={`decline-slot-${s.id}`}><X size={11} />Decline</button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                <div className="glass rounded-2xl p-6">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <Overline>Upcoming booked</Overline>
                      {booked.length > 0 && (
                        <div className="text-xs text-muted-foreground font-mono mt-1">
                          {booked.filter((s) => s.checked_in).length} of {booked.length} checked in
                          {fbSummary && fbSummary.sent > 0 && (
                            <span> · {fbSummary.sent} feedback sent · {fbSummary.responded} responded{fbSummary.nps != null ? ` · NPS ${fbSummary.nps}` : ""}</span>
                          )}
                        </div>
                      )}
                    </div>
                    {booked.length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={sendAllConfirms}
                          disabled={bulkBusy}
                          className="btn-ghost text-xs"
                          data-testid="booked-send-all-confirmations"
                        >
                          <Send size={11} />{bulkBusy ? "Sending…" : `Send confirmation (${booked.length})`}
                        </button>
                        <button
                          onClick={() => sendFeedbackBulk("checked_in")}
                          disabled={fbBulk}
                          className="btn-primary text-xs"
                          data-testid="booked-send-feedback-all"
                          title="Send NPS feedback survey to founders who attended"
                        >
                          <MessageSquare size={11} />{fbBulk ? "Sending…" : `Send feedback survey`}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="mt-4 space-y-2">
                    {booked.length === 0 ? (
                      <div className="text-sm text-muted-foreground">Nothing booked yet.</div>
                    ) : (
                      booked.map((s) => {
                        const bookerName = s.member_summary?.name || s.submitted_name || s.submitted_email || "Booked";
                        const bookerEmail = s.member_summary?.email || s.submitted_email;
                        const hasDetails = s.pitch_note || s.linkedin || s.website || bookerEmail;
                        const bookedSub = hasDetails ? {
                          id: s.submission_id,
                          name: bookerName,
                          email: bookerEmail,
                          linkedin: s.linkedin,
                          website: s.website,
                          pitch_note: s.pitch_note,
                          slot_start_at: s.start_at,
                          slot_end_at: s.end_at,
                          status: "accepted",
                        } : null;
                        return (
                          <div key={s.id} className="flex items-center justify-between p-4 rounded-xl border border-border" data-testid={`booked-${s.id}`}>
                            <button
                              className="flex-1 text-left"
                              onClick={() => bookedSub && setDrawerSub(bookedSub)}
                              disabled={!bookedSub}
                              data-testid={`booked-open-${s.id}`}
                            >
                              <div className="font-medium text-sm">{bookerName}</div>
                              {bookerEmail && <div className="text-xs text-muted-foreground font-mono">{bookerEmail}</div>}
                              <div className="text-xs text-muted-foreground mt-1 font-mono">{fmtDatePT(s.start_at, { weekday: "short", month: "short", day: "numeric" })} · {fmtTimePT(s.start_at)} PT</div>
                              {s.checked_in && s.checked_in_at && (
                                <div className="text-xs mt-1 font-mono" style={{ color: "hsl(var(--vd-green))" }} data-testid={`booked-checkin-time-${s.id}`}>
                                  <Check size={10} className="inline mr-1" />Arrived {fmtTimePT(s.checked_in_at)} PT
                                </div>
                              )}
                              {s.pitch_note && <div className="text-xs mt-2 text-foreground/80 italic line-clamp-2">&ldquo;{s.pitch_note}&rdquo;</div>}
                            </button>
                            <div className="flex flex-col items-end gap-1 ml-3">
                              {s.checked_in ? (
                                <span className="chip" style={{ background: "hsl(var(--vd-green) / 0.15)", color: "hsl(var(--vd-green))" }} data-testid={`booked-checkedin-${s.id}`}>
                                  <Check size={11} />Checked in
                                </span>
                              ) : (
                                <span className="chip">Confirmed</span>
                              )}
                              <button
                                onClick={() => toggleCheckin(s)}
                                disabled={checkinSlot === s.id}
                                className="btn-ghost text-xs"
                                style={s.checked_in ? {} : { color: "hsl(var(--vd-green))" }}
                                data-testid={`booked-checkin-${s.id}`}
                                title={s.checked_in ? "Reverse check-in" : "Mark this founder as checked-in (arrived)"}
                              >
                                <UserCheck size={11} />
                                {checkinSlot === s.id ? "…" : (s.checked_in ? "Undo check-in" : "Check in")}
                              </button>
                              <button
                                onClick={() => sendConfirm(s.id)}
                                disabled={sendingSlot === s.id || !bookerEmail}
                                className="btn-ghost text-xs"
                                data-testid={`booked-send-${s.id}`}
                                title={bookerEmail ? "Send confirmation email + calendar invite" : "No email on file"}
                              >
                                <Mail size={11} />{sendingSlot === s.id ? "Sending…" : "Send confirmation"}
                              </button>
                              {(() => {
                                const fbState = fbSummary?.per_slot?.[s.id];
                                const alreadyResponded = fbState?.status === "responded";
                                const alreadySent = fbState?.status === "sent";
                                return (
                                  <button
                                    onClick={() => sendFeedbackForSlot(s.id)}
                                    disabled={fbSlot === s.id || !bookerEmail || alreadyResponded}
                                    className="btn-ghost text-xs"
                                    data-testid={`booked-feedback-${s.id}`}
                                    title={
                                      !bookerEmail ? "No email on file" :
                                      alreadyResponded ? "Founder already responded" :
                                      alreadySent ? "Resend feedback survey" :
                                      "Send NPS feedback survey to this founder"
                                    }
                                    style={alreadyResponded ? { color: "hsl(var(--vd-green))" } : {}}
                                  >
                                    <MessageSquare size={11} />
                                    {fbSlot === s.id ? "Sending…" : alreadyResponded ? "Responded" : alreadySent ? "Resend feedback" : "Send feedback"}
                                  </button>
                                );
                              })()}
                              {bookedSub && (
                                <button onClick={() => setDrawerSub(bookedSub)} className="btn-ghost text-xs" data-testid={`booked-view-${s.id}`}>
                                  <Eye size={11} />Details
                                </button>
                              )}
                              <button
                                onClick={() => openCancel(s, bookerName, bookerEmail)}
                                className="btn-ghost text-xs"
                                style={{ color: "hsl(var(--vd-coral, 4 84% 60%))" }}
                                data-testid={`booked-remove-${s.id}`}
                                title="Cancel this booking and free the slot"
                              >
                                <Trash2 size={11} />Remove
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      {drawerSub && <CapitalRequestDrawer submission={drawerSub} onClose={() => setDrawerSub(null)} onDecided={() => { loadSlots(); loadSubs(); }} />}

      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cancel-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeCancel} />
          <div className="relative glass-panel w-full max-w-md p-6 rounded-2xl">
            <div className="overline">Remove booking</div>
            <h3 className="mt-2 font-heading text-xl leading-tight">Remove {cancelTarget.bookerName}'s slot?</h3>
            <div className="mt-1 text-xs font-mono text-muted-foreground">
              {fmtDatePT(cancelTarget.slot.start_at, { weekday: "short", month: "short", day: "numeric" })} · {fmtTimePT(cancelTarget.slot.start_at)} PT
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              The slot will become available again for other founders. {cancelTarget.bookerEmail
                ? <>An email {cancelNotify ? "will be sent" : "will not be sent"} to <span className="font-mono">{cancelTarget.bookerEmail}</span> with the reason below.</>
                : "No email is on file for this booking — nothing will be sent."}
            </p>

            <label className="block mt-4 text-xs font-medium text-foreground">Reason (shown to the founder)</label>
            <textarea
              rows={3}
              className="input-field resize-none mt-1"
              placeholder="e.g. Sree had a scheduling conflict this week — please book a new time."
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              data-testid="cancel-reason"
              autoFocus
            />

            {cancelTarget.bookerEmail && (
              <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={cancelNotify}
                  onChange={(e) => setCancelNotify(e.target.checked)}
                  data-testid="cancel-notify"
                />
                Email the founder to let them know
              </label>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={closeCancel} disabled={cancelBusy} className="btn-ghost text-xs" data-testid="cancel-modal-close">
                Keep booking
              </button>
              <button
                onClick={confirmCancel}
                disabled={cancelBusy}
                className="btn-primary text-xs"
                style={{ background: "hsl(var(--vd-coral, 4 84% 60%))" }}
                data-testid="cancel-modal-confirm"
              >
                <Trash2 size={11} />{cancelBusy ? "Removing…" : "Remove booking"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
